import {
  createContext,
  useContext,
  useId,
  type ComponentPropsWithoutRef,
  type JSX,
  type ReactNode,
} from 'react';

import { cx } from './internal/cx.js';

interface RadioGroupContextValue {
  name: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const RadioGroupContext = createContext<RadioGroupContextValue | null>(null);

function useRadioGroupContext(): RadioGroupContextValue {
  const ctx = useContext(RadioGroupContext);
  if (!ctx) throw new Error('Radio must be used within RadioGroup');
  return ctx;
}

export interface RadioGroupProps extends Omit<ComponentPropsWithoutRef<'div'>, 'onChange'> {
  children: ReactNode;
  name?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

/** Group of mutually exclusive radio options with shared name. */
export function RadioGroup({
  children,
  className,
  name: nameProp,
  value,
  onChange,
  disabled,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  ...props
}: RadioGroupProps): JSX.Element {
  const generatedName = useId();
  const name = nameProp ?? generatedName.replace(/:/g, '');

  return (
    <RadioGroupContext.Provider
      value={{ name, value, onChange, ...(disabled !== undefined ? { disabled } : {}) }}
    >
      <div
        role="radiogroup"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        className={cx('nrx-radio-group', className)}
        {...props}
      >
        {children}
      </div>
    </RadioGroupContext.Provider>
  );
}

export interface RadioProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'name'> {
  value: string;
  label?: ReactNode;
}

/** Single radio option; use inside RadioGroup. */
export function Radio({
  value,
  label,
  className,
  disabled,
  id,
  ...props
}: RadioProps): JSX.Element {
  const ctx = useRadioGroupContext();
  const inputId = id ?? `${ctx.name}-${value}`;
  const checked = ctx.value === value;
  const isDisabled = disabled ?? ctx.disabled;

  const input = (
    <input
      type="radio"
      id={inputId}
      name={ctx.name}
      value={value}
      checked={checked}
      disabled={isDisabled}
      className={cx('nrx-radio', className)}
      onChange={() => ctx.onChange(value)}
      {...props}
    />
  );

  if (label == null) return input;

  return (
    <label htmlFor={inputId} className="nrx-radio-label">
      {input}
      <span className="nrx-radio-label__text">{label}</span>
    </label>
  );
}
